import type { DocumentNode } from "graphql";
import { Kind, getOperationAST } from "graphql";

import {
  allocateIndividualRealTimeOrgChart,
  bulkMoveIndividualsRealTimeOrgChart,
} from "src/allocation/graphql/allocation.graphql";
import {
  addPosition,
  updatePosition,
} from "src/allocation/graphql/members.graphql";
import { changePersonSupplyGroup } from "src/allocation/graphql/person.graphql";
import {
  markGroupAsHidden as markGroupAsHiddenAllocationTeam,
  RemoveTeamSubtreeFromOrg,
} from "src/allocation/graphql/team.graphql";
import { addAssociation as addAssociationMutation } from "src/components/AddTeamAssociationModal/queries/addAssociation.graphql";
import { updatePersonFields as updatePersonFieldsMutation } from "src/components/OrgChart/queries/updatePersonFields.graphql";
import {
  addNewTagToEntity as addNewTagToEntityTagsEditor,
  addTagToEntity as addTagToEntityTagsEditor,
  removeTagFromEntity as removeTagFromEntityTagsEditor,
  updateEntityTag as updateEntityTagTagsEditor,
  updateTagValue as updateTagValueTagsEditor,
} from "src/components/TagsEditor/queries/tags.graphql";
import { markGroupAsHidden as markGroupAsHiddenAllocation } from "src/queries/allocation.graphql";
import {
  addMembership,
  addMembershipOnly,
  fillPositionVacancy,
  removeMembership,
  updateMembership,
  vacatePosition,
} from "src/queries/memberships.graphql";
import {
  createTeam,
  moveGroupOrgChart,
  moveGroupRealTime,
  updateGroupDailyRate,
  updateGroupName,
  updateGroupType,
  updateGroupTypeRestricted,
} from "src/queries/mutations/group.graphql";
import { updatePersonAttributes } from "src/queries/mutations/updatePersonAttributes.graphql";
import { updatePersonManager } from "src/queries/mutations/updatePersonManager.graphql";
import { updateTeamAttributes } from "src/queries/mutations/updateTeamAttributes.graphql";
import {
  removeAssociation,
  updateTagValue as updateTagValueQueries,
} from "src/queries/tags.graphql";

import {
  addAssociationV2WithOption,
  addMembershipWithOption,
  addNewTagToEntityWithOption,
  addPositionWithOption,
  addTagToEntityWithOption,
  allocateIndividualRealTimeWithOption,
  bulkMoveIndividualsRealTimeWithOption,
  changePersonSupplyGroupWithOption,
  createTeamWithOption,
  fillPositionVacancyWithOption,
  markGroupAsHiddenWithOption,
  moveGroupRealTimeWithOption,
  removeAssociationWithOption,
  removeMembershipWithOption,
  removeTagFromEntityWithOption,
  removeTeamSubtreeFromOrgWithOption,
  updateEntityTagWithOption,
  updateGroupAttributesWithOption,
  updateGroupDailyRateWithOption,
  updateGroupNameWithOption,
  updateGroupTypeRestrictedWithOption,
  updateGroupTypeWithOption,
  updateMembershipWithOption,
  updatePersonAttributesWithOption,
  updatePersonFieldsWithOption,
  updatePersonManagerWithOption,
  updatePositionWithOption,
  updateTagValueWithOption,
  vacatePositionWithOption,
} from "./designOptionGraphql";

export type OverlayMutationInventoryEntry = {
  /** Live GraphQL operation name (no WithOption suffix). */
  operation: string;
  /** JS export name of the live document in its .graphql module. */
  liveExportName: string;
  /** Relative path to the live .graphql document. */
  liveDoc: string;
  liveMutation: DocumentNode;
  twinMutation: DocumentNode;
  /** Known org-chart / Details / overlay write call sites for this live document. */
  callSites: readonly string[];
};

/** Live mutations with no *WithOption twin — documented gaps, not paired. */
export type OverlayMutationGap = {
  operation: string;
  liveDoc: string;
  reason: string;
  callSites: readonly string[];
};

const pair = (
  operation: string,
  liveExportName: string,
  liveDoc: string,
  liveMutation: DocumentNode,
  twinMutation: DocumentNode,
  callSites: readonly string[]
): OverlayMutationInventoryEntry => ({
  operation,
  liveExportName,
  liveDoc,
  liveMutation,
  twinMutation,
  callSites,
});

/**
 * Authoritative inventory of live → *WithOption pairs used by org-chart and
 * Details overlay writes. Drives overlayPairing and CI completeness guards.
 */
export const OVERLAY_MUTATION_INVENTORY: OverlayMutationInventoryEntry[] = [
  pair(
    "updateGroupName",
    "updateGroupName",
    "src/queries/mutations/group.graphql",
    updateGroupName,
    updateGroupNameWithOption,
    [
      "src/pages/Designs/overlay/teamIdentityWrites.ts",
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamName.ts",
    ]
  ),
  pair(
    "createTeam",
    "createTeam",
    "src/queries/mutations/group.graphql",
    createTeam,
    createTeamWithOption,
    [
      "src/pages/Designs/overlay/teamIdentityWrites.ts",
      "src/components/OrgChart/controller/reportingLineDnd/runDropAction.ts",
    ]
  ),
  pair(
    "updateGroupType",
    "updateGroupType",
    "src/queries/mutations/group.graphql",
    updateGroupType,
    updateGroupTypeWithOption,
    []
  ),
  pair(
    "updateGroupTypeRestricted",
    "updateGroupTypeRestricted",
    "src/queries/mutations/group.graphql",
    updateGroupTypeRestricted,
    updateGroupTypeRestrictedWithOption,
    ["src/components/TeamDetailPage/ActionsMenu/Modals/ChangeTeamTypeModal.js"]
  ),
  pair(
    "updateGroupAttributes",
    "updateTeamAttributes",
    "src/queries/mutations/updateTeamAttributes.graphql",
    updateTeamAttributes,
    updateGroupAttributesWithOption,
    [
      "src/pages/Designs/overlay/teamIdentityWrites.ts",
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamAttributes.ts",
    ]
  ),
  pair(
    "updateGroupDailyRate",
    "updateGroupDailyRate",
    "src/queries/mutations/group.graphql",
    updateGroupDailyRate,
    updateGroupDailyRateWithOption,
    [
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamDailyRate.ts",
    ]
  ),
  pair(
    "updatePersonAttributes",
    "updatePersonAttributes",
    "src/queries/mutations/updatePersonAttributes.graphql",
    updatePersonAttributes,
    updatePersonAttributesWithOption,
    [
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/PersonDetails/hooks/useUpdatePersonCustomAttributes.ts",
    ]
  ),
  pair(
    "updatePersonManager",
    "updatePersonManager",
    "src/queries/mutations/updatePersonManager.graphql",
    updatePersonManager,
    updatePersonManagerWithOption,
    [
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamOwner.ts",
      "src/components/OrgChart/controller/dnd/OrgChartDnDContext.tsx",
      "src/components/OrgChart/controller/reportingLineDnd/runDropAction.ts",
    ]
  ),
  pair(
    "changePersonSupplyGroup",
    "changePersonSupplyGroup",
    "src/allocation/graphql/person.graphql",
    changePersonSupplyGroup,
    changePersonSupplyGroupWithOption,
    [
      "src/components/OrgChart/controller/dnd/OrgChartDnDContext.tsx",
      "src/hooks/useAllocatePersonToGroup.tsx",
    ]
  ),
  pair(
    "markGroupAsHidden",
    "markGroupAsHidden",
    "src/allocation/graphql/team.graphql",
    markGroupAsHiddenAllocationTeam,
    markGroupAsHiddenWithOption,
    [
      "src/components/HideTeamModal/index.js",
      "src/components/OrgChart/controller/dnd/OrgChartDnDContext.tsx",
    ]
  ),
  pair(
    "markGroupAsHidden",
    "markGroupAsHidden",
    "src/queries/allocation.graphql",
    markGroupAsHiddenAllocation,
    markGroupAsHiddenWithOption,
    []
  ),
  pair(
    "removeTeamSubtreeFromOrg",
    "RemoveTeamSubtreeFromOrg",
    "src/allocation/graphql/team.graphql",
    RemoveTeamSubtreeFromOrg,
    removeTeamSubtreeFromOrgWithOption,
    ["src/components/OrgChart/controller/dnd/OrgChartDnDContext.tsx"]
  ),
  pair(
    "bulkMoveIndividualsRealTime",
    "bulkMoveIndividualsRealTimeOrgChart",
    "src/allocation/graphql/allocation.graphql",
    bulkMoveIndividualsRealTimeOrgChart,
    bulkMoveIndividualsRealTimeWithOption,
    [
      "src/components/BulkMoveMembersModal/hooks/useBulkMoveMembers.js",
      "src/components/OrgChart/controller/dnd/useAllocatePersonToTeam.ts",
      "src/components/OrgChart/controller/reportingLineDnd/runDropAction.ts",
    ]
  ),
  pair(
    "allocateIndividualRealTime",
    "allocateIndividualRealTimeOrgChart",
    "src/allocation/graphql/allocation.graphql",
    allocateIndividualRealTimeOrgChart,
    allocateIndividualRealTimeWithOption,
    ["src/components/OrgChart/controller/dnd/OrgChartDnDContext.tsx"]
  ),
  pair(
    "updateEntityTag",
    "updateEntityTag",
    "src/components/TagsEditor/queries/tags.graphql",
    updateEntityTagTagsEditor,
    updateEntityTagWithOption,
    ["src/components/TagsEditor/sub-components/TagAttributesModal.js"]
  ),
  pair(
    "moveGroupRealTime",
    "moveGroupRealTime",
    "src/queries/mutations/group.graphql",
    moveGroupRealTime,
    moveGroupRealTimeWithOption,
    [
      "src/components/MoveGroupModal/index.js",
      "src/components/MoveGroupEnhancedModal/index.js",
    ]
  ),
  pair(
    "moveGroupRealTime",
    "moveGroupOrgChart",
    "src/queries/mutations/group.graphql",
    moveGroupOrgChart,
    moveGroupRealTimeWithOption,
    [
      "src/components/OrgChart/controller/dnd/OrgChartDnDContext.tsx",
      "src/components/OrgChart/controller/reportingLineDnd/runDropAction.ts",
    ]
  ),
  pair(
    "addMembership",
    "addMembership",
    "src/queries/memberships.graphql",
    addMembership,
    addMembershipWithOption,
    [
      "src/components/PersonDetailPage/PersonMemberships/hooks/useMemberships.js",
    ]
  ),
  pair(
    "addMembership",
    "addMembershipOnly",
    "src/queries/memberships.graphql",
    addMembershipOnly,
    addMembershipWithOption,
    [
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamOwner.ts",
      "src/components/OrgChart/controller/dnd/OrgChartDnDContext.tsx",
      "src/components/OrgChart/controller/reportingLineDnd/runDropAction.ts",
      "src/pages/Designs/overlay/useOverlayRosterAdd.ts",
      "src/hooks/useAllocatePersonToGroup.tsx",
    ]
  ),
  pair(
    "removeMembership",
    "removeMembership",
    "src/queries/memberships.graphql",
    removeMembership,
    removeMembershipWithOption,
    [
      "src/components/PersonDetailPage/PersonMemberships/hooks/useMemberships.js",
      "src/components/OrgChart/utils/removeOwnerMembership.ts",
    ]
  ),
  pair(
    "updateMembership",
    "updateMembership",
    "src/queries/memberships.graphql",
    updateMembership,
    updateMembershipWithOption,
    [
      "src/components/PersonDetailPage/PersonMemberships/hooks/useMemberships.js",
      "src/components/BulkMoveMembersModal/hooks/useBulkMoveMembers.js",
      "src/components/OrgChart/controller/dnd/orgChartMembershipRemoval.ts",
    ]
  ),
  pair(
    "fillPositionVacancy",
    "fillPositionVacancy",
    "src/queries/memberships.graphql",
    fillPositionVacancy,
    fillPositionVacancyWithOption,
    [
      "src/components/PersonDetailPage/PersonMemberships/hooks/useFillPosition.js",
      "src/components/PersonDetailPage/PersonMemberships/hooks/useMemberships.js",
      "src/components/OrgChart/controller/dnd/OrgChartDnDContext.tsx",
    ]
  ),
  pair(
    "vacatePosition",
    "vacatePosition",
    "src/queries/memberships.graphql",
    vacatePosition,
    vacatePositionWithOption,
    [
      "src/components/PersonDetailPage/PersonMemberships/hooks/useVacatePosition.ts",
      "src/components/PersonDetailPage/PersonMemberships/hooks/useMemberships.js",
      "src/components/OrgChart/controller/dnd/OrgChartDnDContext.tsx",
    ]
  ),
  pair(
    "addPosition",
    "addPosition",
    "src/allocation/graphql/members.graphql",
    addPosition,
    addPositionWithOption,
    ["src/components/AddPositionModal/index.js"]
  ),
  pair(
    "updatePosition",
    "updatePosition",
    "src/allocation/graphql/members.graphql",
    updatePosition,
    updatePositionWithOption,
    [
      "src/components/EditPositionModal/index.js",
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamOwner.ts",
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/PersonDetails/hooks/useUpdateDetailsFte.ts",
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/PersonDetails/hooks/useUpdatePositionName.ts",
      "src/components/OrgChart/controller/dnd/OrgChartDnDContext.tsx",
      "src/components/OrgChart/controller/reportingLineDnd/runDropAction.ts",
    ]
  ),
  pair(
    "addTagToEntity",
    "addTagToEntity",
    "src/components/TagsEditor/queries/tags.graphql",
    addTagToEntityTagsEditor,
    addTagToEntityWithOption,
    [
      "src/components/TagsEditor/TagsEditorContainer.js",
      "src/components/AddVacantRoleModal/index.js",
      "src/pages/TagDetailsPage/sub-components/hooks/useAddTagToEntity.js",
    ]
  ),
  pair(
    "addNewTagToEntity",
    "addNewTagToEntity",
    "src/components/TagsEditor/queries/tags.graphql",
    addNewTagToEntityTagsEditor,
    addNewTagToEntityWithOption,
    ["src/components/TagsEditor/TagsEditorContainer.js"]
  ),
  pair(
    "removeTagFromEntity",
    "removeTagFromEntity",
    "src/components/TagsEditor/queries/tags.graphql",
    removeTagFromEntityTagsEditor,
    removeTagFromEntityWithOption,
    ["src/components/TagsEditor/TagsEditorContainer.js"]
  ),
  pair(
    "addAssociationV2",
    "addAssociation",
    "src/components/AddTeamAssociationModal/queries/addAssociation.graphql",
    addAssociationMutation,
    addAssociationV2WithOption,
    ["src/components/AddTeamAssociationModal/index.js"]
  ),
  pair(
    "removeAssociation",
    "removeAssociation",
    "src/queries/tags.graphql",
    removeAssociation,
    removeAssociationWithOption,
    [
      "src/components/AddTeamAssociationModal/index.js",
      "src/components/TeamAssociationsPanel/RemoveAssociationModal.js",
    ]
  ),
  pair(
    "updatePersonFields",
    "updatePersonFields",
    "src/components/OrgChart/queries/updatePersonFields.graphql",
    updatePersonFieldsMutation,
    updatePersonFieldsWithOption,
    [
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/PersonDetails/hooks/useUpdatePersonFields.ts",
    ]
  ),
  pair(
    "updateTag",
    "updateTagValue",
    "src/components/TagsEditor/queries/tags.graphql",
    updateTagValueTagsEditor,
    updateTagValueWithOption,
    ["src/components/TagsEditor/sub-components/TagAttributesModal.js"]
  ),
  pair(
    "updateTag",
    "updateTagValue",
    "src/queries/tags.graphql",
    updateTagValueQueries,
    updateTagValueWithOption,
    ["src/pages/TagDetailsPage/sub-components/hooks/useTagBgImage.js"]
  ),
];

/** Mutations reachable during design-option editing that have no *WithOption twin. */
export const OVERLAY_MUTATION_GAPS: OverlayMutationGap[] = [
  {
    operation: "mutateMemberships",
    liveDoc: "src/queries/memberships.graphql",
    reason:
      "Batch membership API has no *WithOption twin; use single-membership twins instead.",
    callSites: [
      "src/components/PersonDetailPage/PersonMemberships/hooks/useMemberships.js",
      "src/components/BulkMoveMembersModal/hooks/useBulkMoveMembers.js",
    ],
  },
  {
    operation: "recreatePosition",
    liveDoc: "src/allocation/graphql/members.graphql",
    reason:
      "Position DnD S1 uses live-only recreatePosition; no backend twin yet.",
    callSites: [
      "src/components/OrgChart/controller/dnd/OrgChartDnDContext.tsx",
    ],
  },
];

/** All overlay *WithOption operation names declared in withOptionMutations.graphql. */
export const OVERLAY_TWIN_OPERATIONS = [
  "updateGroupNameWithOption",
  "createTeamWithOption",
  "updateGroupTypeWithOption",
  "updateGroupTypeRestrictedWithOption",
  "updateGroupAttributesWithOption",
  "updateGroupDailyRateWithOption",
  "updatePersonAttributesWithOption",
  "updatePersonManagerWithOption",
  "changePersonSupplyGroupWithOption",
  "markGroupAsHiddenWithOption",
  "removeTeamSubtreeFromOrgWithOption",
  "bulkMoveIndividualsRealTimeWithOption",
  "allocateIndividualRealTimeWithOption",
  "updateEntityTagWithOption",
  "moveGroupRealTimeWithOption",
  "addMembershipWithOption",
  "removeMembershipWithOption",
  "updateMembershipWithOption",
  "fillPositionVacancyWithOption",
  "vacatePositionWithOption",
  "addPositionWithOption",
  "updatePositionWithOption",
  "addTagToEntityWithOption",
  "addNewTagToEntityWithOption",
  "removeTagFromEntityWithOption",
  "addAssociationV2WithOption",
  "removeAssociationWithOption",
  "updatePersonFieldsWithOption",
  "updateTagValueWithOption",
] as const;

/** Resolve the mutation operation name from a GraphQL document (not definitions[0]). */
export const getMutationOperationName = (
  document: DocumentNode
): string | undefined => {
  const fromAst = getOperationAST(document)?.name?.value;
  if (fromAst) return fromAst;

  for (const definition of document.definitions) {
    if (
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.name?.value
    ) {
      return definition.name.value;
    }
  }
  return undefined;
};

/** Org-chart / Details scopes that must route inventory writes through overlayMutate. */
export const OVERLAY_WRITE_GUARDED_SCOPES = [
  "src/components/OrgChart",
  "src/components/PersonDetailPage/PersonMemberships",
  "src/components/BulkMoveMembersModal",
  "src/components/MoveGroupModal",
  "src/components/MoveGroupEnhancedModal",
  "src/components/HideTeamModal",
  "src/components/AddPositionModal",
  "src/components/EditPositionModal",
  "src/components/AddVacantRoleModal",
  "src/components/AddTeamAssociationModal",
  "src/components/TeamAssociationsPanel",
  "src/components/TeamDetailPage/ActionsMenu/Modals",
  "src/components/TagsEditor",
  "src/pages/TagDetailsPage/sub-components/hooks",
] as const;

const OVERLAY_WRITE_ROUTER_MARKERS = [
  "overlayMutate",
  "useOverlayMutate",
  "useTeamIdentityWrites",
] as const;

/** Files allowed to call client.mutate with inventory live docs on trunk-only paths. */
export const OVERLAY_TRUNK_MUTATE_ALLOWLIST = new Set([
  "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamOwner.ts",
  "src/components/OrgChart/utils/removeOwnerMembership.ts",
]);

export const buildOverlayTwinMap = (): Map<DocumentNode, DocumentNode> => {
  const map = new Map<DocumentNode, DocumentNode>();
  for (const entry of OVERLAY_MUTATION_INVENTORY) {
    map.set(entry.liveMutation, entry.twinMutation);
  }
  return map;
};

export const allOverlayWriteCallSites = (): string[] =>
  Array.from(
    new Set(OVERLAY_MUTATION_INVENTORY.flatMap((entry) => [...entry.callSites]))
  ).sort();

export const usesOverlayWriteRouter = (source: string): boolean =>
  OVERLAY_WRITE_ROUTER_MARKERS.some((marker) => source.includes(marker));
